import { useCallback, useEffect, useRef } from 'react';

type UseMetronomeOptions = {
  enabled: boolean;
  bpm: number;
  accentEvery?: number;
  muted?: boolean;
  volume?: number;
};

const LOOKAHEAD_MS = 25;
const SCHEDULE_AHEAD_S = 0.12;

export function useMetronome({
  enabled,
  bpm,
  accentEvery = 1,
  muted = false,
  volume = 0.05,
}: UseMetronomeOptions) {
  const audioContextRef = useRef<AudioContext | null>(null);
  const schedulerRef = useRef<number | null>(null);
  const nextNoteTimeRef = useRef(0);
  const beatRef = useRef(0);

  const stopScheduler = useCallback(() => {
    if (schedulerRef.current != null) {
      window.clearInterval(schedulerRef.current);
      schedulerRef.current = null;
    }
  }, []);

  const scheduleClick = useCallback((when: number, accented: boolean) => {
    const context = audioContextRef.current;
    if (!context) return;

    const oscillator = context.createOscillator();
    const gainNode = context.createGain();

    oscillator.type = 'square';
    oscillator.frequency.setValueAtTime(accented ? 1320 : 920, when);

    gainNode.gain.setValueAtTime(0.0001, when);
    gainNode.gain.exponentialRampToValueAtTime(volume, when + 0.003);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, when + 0.055);

    oscillator.connect(gainNode);
    gainNode.connect(context.destination);

    oscillator.start(when);
    oscillator.stop(when + 0.06);
  }, [volume]);

  const ensureContext = useCallback(async () => {
    if (typeof window === 'undefined') return;

    const AudioContextCtor =
      window.AudioContext ??
      (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;

    if (!AudioContextCtor) return;

    if (!audioContextRef.current) {
      audioContextRef.current = new AudioContextCtor();
    }

    if (audioContextRef.current.state === 'suspended') {
      await audioContextRef.current.resume();
    }

    const context = audioContextRef.current;
    if (!context || context.state !== 'running') return;

    // Mobile browsers often require a source node to start inside the user gesture.
    const unlockOscillator = context.createOscillator();
    const unlockGain = context.createGain();
    unlockGain.gain.setValueAtTime(0.0001, context.currentTime);
    unlockOscillator.connect(unlockGain);
    unlockGain.connect(context.destination);
    unlockOscillator.start(context.currentTime);
    unlockOscillator.stop(context.currentTime + 0.01);
  }, []);

  useEffect(() => {
    stopScheduler();

    if (!enabled || muted || bpm <= 0) return;

    const context = audioContextRef.current;
    if (!context || context.state !== 'running') return;

    const secondsPerBeat = 60 / bpm;
    beatRef.current = 0;
    nextNoteTimeRef.current = context.currentTime + 0.05;

    schedulerRef.current = window.setInterval(() => {
      const currentContext = audioContextRef.current;
      if (!currentContext || currentContext.state !== 'running') return;

      while (nextNoteTimeRef.current < currentContext.currentTime + SCHEDULE_AHEAD_S) {
        beatRef.current += 1;
        scheduleClick(nextNoteTimeRef.current, accentEvery > 1 ? beatRef.current % accentEvery === 1 : true);
        nextNoteTimeRef.current += secondsPerBeat;
      }
    }, LOOKAHEAD_MS);

    return stopScheduler;
  }, [accentEvery, bpm, enabled, muted, scheduleClick, stopScheduler]);

  useEffect(() => {
    return () => {
      stopScheduler();
      const context = audioContextRef.current;
      audioContextRef.current = null;
      if (context && context.state !== 'closed') {
        void context.close().catch(() => undefined);
      }
    };
  }, [stopScheduler]);

  return { ensureContext };
}
